import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dataService } from '../../services/dataService';
import { StudentGroup, Student, LearningPath } from '../../data/types';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Toggle from '../ui/Toggle';

const StudentGroupDetailScreen: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<StudentGroup | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [learningPaths, setLearningPaths] = useState<LearningPath[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddingStudents, setIsAddingStudents] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [classFilter, setClassFilter] = useState('All Classes');
  const [classes, setClasses] = useState<{id: string, name: string}[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!groupId) return;
      
      try {
        setLoading(true);
        const [groupData, studentsData, classesData, pathsData] = await Promise.all([
          dataService.getStudentGroup(groupId),
          dataService.getStudents(),
          dataService.getClasses(),
          dataService.getLearningPaths()
        ]);
        
        if (!groupData) {
          console.error('Group not found');
          navigate('/student-groups');
          return;
        }
        
        setGroup(groupData);
        setAllStudents(studentsData);
        setLearningPaths(pathsData);
        
        // Get students in this group
        const groupStudents = studentsData.filter(student => 
          groupData.students.includes(student.id)
        );
        setStudents(groupStudents);
        
        setClasses(classesData.map(cls => ({
          id: cls.id,
          name: cls.name
        })));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [groupId, navigate]);

  const handleBack = () => {
    navigate('/student-groups');
  };

  const handleEdit = () => {
    if (groupId) {
      navigate(`/student-groups/${groupId}/edit`);
    }
  };
  
  const handleToggleStatus = async () => {
    if (!group) return;
    
    try {
      const updatedGroup = await dataService.toggleGroupStatus(group.id);
      setGroup(updatedGroup);
    } catch (error) {
      console.error('Error toggling group status:', error);
    }
  };
  
  const calculateGroupProgress = () => {
    if (!group || students.length === 0) return 0;
    
    // For mock purposes, calculate a random progress percentage
    // In a real app, this would be calculated based on student progress in the group
    return Math.floor(Math.random() * 100);
  };
  
  const getLearningPaths = () => {
    if (!group || !group.learningPaths || group.learningPaths.length === 0) {
      return ['Not Assigned'];
    }
    
    // Map all learning path IDs to their names
    return group.learningPaths.map(pathId => {
      const path = learningPaths.find(p => p.id === pathId);
      return path ? path.name : 'Unknown Path';
    });
  };

  const handleAddStudents = () => {
    setIsAddingStudents(true);
    setSelectedStudentIds([]);
  };

  const handleCancelAddStudents = () => {
    setIsAddingStudents(false);
    setSearchQuery('');
    setClassFilter('All Classes');
  };

  const handleStudentSelection = (studentId: string) => {
    setSelectedStudentIds(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSaveStudents = async () => {
    if (!group) return;
    
    try {
      // Add selected students to the group without using Set
      // Create a unique array by filtering out duplicates
      const combinedIds = [...group.students, ...selectedStudentIds];
      const updatedStudentIds = combinedIds.filter((id, index) => 
        combinedIds.indexOf(id) === index
      );

      const updatedGroup = {
        ...group,
        students: updatedStudentIds
      };

      await dataService.updateStudentGroup(updatedGroup);

      // Update local state
      setGroup(updatedGroup);

      // Update students list
      const updatedStudents = allStudents.filter(student => 
        updatedStudentIds.includes(student.id)
      );
      setStudents(updatedStudents);

      // Exit add mode
      setIsAddingStudents(false);
      setSearchQuery('');
      setClassFilter('All Classes');
    } catch (error) {
      console.error('Error updating group:', error);
    }
  };

  const handleRemoveStudent = async (studentId: string) => {
    if (!group) return;

    try {
      // Remove student from the group
      const updatedStudentIds = group.students.filter(id => id !== studentId);

      const updatedGroup = {
        ...group,
        students: updatedStudentIds
      };

      await dataService.updateStudentGroup(updatedGroup);

      // Update local state
      setGroup(updatedGroup);

      // Update students list
      setStudents(students.filter(student => student.id !== studentId));
    } catch (error) {
      console.error('Error removing student from group:', error);
    }
  };

  // Filter students for adding to the group
  const getFilteredStudents = () => {
    if (!group) return [];

    // Filter out students already in the group
    const availableStudents = allStudents.filter(student => 
      !group.students.includes(student.id)
    );

    return availableStudents.filter(student => {
      const matchesSearch = 
        searchQuery === '' ||
        student.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        student.id.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesClass = 
        classFilter === 'All Classes' || 
        student.classes.some(cls => {
          const classObj = classes.find(c => c.id === cls);
          return classObj && classObj.id === classFilter;
        });

      return matchesSearch && matchesClass;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="text-center py-10">
        <h3 className="mt-2 text-lg font-medium text-gray-900">Group not found</h3>
        <Button variant="primary" className="mt-4" onClick={handleBack}>
          Back to Groups
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="mb-3">
        {/* Header section with back button, title, and actions */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div className="flex items-start space-x-3 max-w-3xl">
            <button 
              onClick={handleBack}
              className="inline-flex items-center p-1 border border-transparent rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none mt-1.5"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold text-gray-900">{group.name}</h1>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="primary" size="sm">
                    {group.students.length} students
                  </Badge>
                  <Badge variant={group.status === 'active' ? 'success' : 'error'} size="sm">
                    {group.status === 'active' ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
              {/* Description with proper wrapping for long text */}
              <p className="text-gray-600 text-sm mt-1 pr-4 break-words mb-2">{group.description}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-2 md:mt-0 md:flex-nowrap">
            <div className="flex items-center mr-2">
              <Toggle 
                checked={group.status === 'active'} 
                onChange={handleToggleStatus} 
                label="Active"
              />
            </div>
            <Button variant="secondary" onClick={handleEdit}>
              Edit Group
            </Button>
            {!isAddingStudents && (
              <Button variant="primary" onClick={handleAddStudents}>
                Add Students
              </Button>
            )}
          </div>
        </div>
      </div>
      
      <Card className="mt-0">
        <div className="p-4">
          {/* Group info section with improved layout for long content */}
          <div className="flex flex-col space-y-3 bg-gray-50 p-3 rounded-md mb-3">
            <div className="flex flex-wrap text-sm gap-x-6 gap-y-2">
              <div>
                <span className="font-medium text-gray-500">ID:</span>{' '}
                <span className="text-gray-900">{group.id}</span>
              </div>
              <div>
                <span className="font-medium text-gray-500">Created:</span>{' '}
                <span className="text-gray-900">{new Date(group.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-start gap-3 pt-1">
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-start gap-1">
                  <span className="text-sm font-medium text-gray-500 whitespace-nowrap mt-0.5">Learning Paths:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1">
                    {getLearningPaths().map((pathName, index) => (
                      <span key={index} className="text-sm text-gray-900 inline-flex items-center">
                        {index > 0 && <span className="text-gray-400 mx-1">•</span>}
                        <span className="truncate max-w-[250px] hover:text-clip hover:overflow-visible" title={pathName}>
                          {pathName}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center whitespace-nowrap">
                <span className="text-sm font-medium text-gray-500 mr-2">Progress:</span>
                <div className="w-24 bg-gray-200 rounded-full h-2.5 mx-2">
                  <div 
                    className="bg-primary h-2.5 rounded-full" 
                    style={{ width: `${calculateGroupProgress()}%` }}
                  ></div>
                </div>
                <span className="text-xs text-gray-500">{calculateGroupProgress()}%</span>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-3 mt-2">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-medium text-gray-900">Students in this Group</h2>
              {!isAddingStudents && students.length > 0 && (
                <Button variant="primary" size="sm" onClick={handleAddStudents}>
                  Add Students
                </Button>
              )}
            </div>
            
            {isAddingStudents ? (
              <div className="space-y-4">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
                  <div className="flex flex-col sm:flex-row gap-4 flex-1">
                    <div className="relative flex-grow">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                        placeholder="Search students by name or ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    
                    <select
                      className="block w-full sm:w-48 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                      value={classFilter}
                      onChange={(e) => setClassFilter(e.target.value)}
                    >
                      <option value="All Classes">All Classes</option>
                      {classes.map(cls => (
                        <option key={cls.id} value={cls.id}>{cls.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button variant="tertiary" onClick={handleCancelAddStudents}>
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={handleSaveStudents}
                      disabled={selectedStudentIds.length === 0}
                    >
                      Add {selectedStudentIds.length} Students
                    </Button>
                  </div>
                </div>
                
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Select
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Student
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ID
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Grade
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Class
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFilteredStudents().map(student => {
                        const studentClass = student.classes.length > 0 
                          ? classes.find(c => c.id === student.classes[0])
                          : null;
                          
                        return (
                          <tr key={student.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                checked={selectedStudentIds.includes(student.id)}
                                onChange={() => handleStudentSelection(student.id)}
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  {student.avatar ? (
                                    <img 
                                      className="h-10 w-10 rounded-full" 
                                      src={student.avatar} 
                                      alt={`${student.firstName} ${student.lastName}`} 
                                    />
                                  ) : (
                                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                      <span className="text-sm font-medium text-gray-600">
                                        {student.firstName.charAt(0)}{student.lastName.charAt(0)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {student.firstName} {student.lastName}
                                  </div>
                                  <div className="text-sm text-gray-500">{student.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{student.id}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">Grade {student.grade}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {studentClass ? studentClass.name : 'Not Assigned'}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <>
                {students.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">No students in this group yet</p>
                    <Button variant="primary" onClick={handleAddStudents}>
                      Add Students
                    </Button>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Student
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ID
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Grade
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Class
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {students.map(student => {
                          const studentClass = student.classes.length > 0 
                            ? classes.find(c => c.id === student.classes[0])
                            : null;
                            
                          return (
                            <tr key={student.id}>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-10 w-10">
                                    {student.avatar ? (
                                      <img 
                                        className="h-10 w-10 rounded-full" 
                                        src={student.avatar} 
                                        alt={`${student.firstName} ${student.lastName}`} 
                                      />
                                    ) : (
                                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                        <span className="text-sm font-medium text-gray-600">
                                          {student.firstName.charAt(0)}{student.lastName.charAt(0)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {student.firstName} {student.lastName}
                                    </div>
                                    <div className="text-sm text-gray-500">{student.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{student.id}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">Grade {student.grade}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {studentClass ? studentClass.name : 'Not Assigned'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button 
                                  className="text-red-600 hover:text-red-900"
                                  onClick={() => handleRemoveStudent(student.id)}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default StudentGroupDetailScreen;
